'use client';

import { GroqImage } from '@/helpers/Image';
import { GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { PortableText, PortableTextBlock } from '@portabletext/react';
import { motion } from 'framer-motion';
import Link from 'next/link';

interface ISocialMediaProps {
  label: string;
  title: {
    lineOne: string;
    lineTwo: string;
  };
  titleMobile: PortableTextBlock[];
  socialLinks: {
    icon: GROQImage;
    label: string;
    link: string;
  }[];
}

export default function SocialMedia({
  label,
  title,
  titleMobile,
  socialLinks,
}: ISocialMediaProps) {
  return (
    <section className="lg:py-[37.5rem] py-[7rem] bg-black">
      <div className="container flex flex-col lg:items-center gap-[6.12rem] lg:gap-[8.5rem]">
        <div className="flex items-start lg:items-center flex-col gap-[3rem]">
          {label && (
            <motion.p
              className="text-center font-nunito text-[1.625rem] tracking-[0.53625rem] text-[#f2f0ef] opacity-70 uppercase"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 0.7, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {label}
            </motion.p>
          )}

          {title && (
            <h2 className="text-[6rem] text-[#dadada] text-left lg:w-[130.375rem]">
              <motion.span
                className="italic uppercase font-newsreader font-[300] hidden lg:block"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                {title.lineOne}
              </motion.span>
              <motion.span
                className="uppercase font-nunito font-[200] lg:!translate-x-[35%] hidden lg:block"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {title.lineTwo}
              </motion.span>

              <motion.span
                className="wtext-white prose-headings:font-nunito prose-em:!font-newsreader prose-headings:text-[6rem] prose-headings:leading-[120%] prose-headings:uppercase prose-headings:font-[200] prose-headings:mb-0 lg:hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <PortableText value={titleMobile} components={richTextComponent} />
              </motion.span>
            </h2>
          )}
        </div>

        <div className="flex lg:flex-row flex-col items-stretch gap-[2rem] w-full lg:w-fit">
          {socialLinks?.map((social, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{
                duration: 0.6,
                delay: 0.3 + index * 0.1,
                ease: [0.76, 0, 0.24, 1],
              }}
              className=""
            >
              <Link
                href={social.link}
                target="_blank"
                rel="noopener noreferrer"
                className="py-[4rem] px-[5rem] rounded-[0.5rem] border-[.125rem] border-white/[0.08] bg-[#F2F0EF]/[2.4%] hover:bg-white/[4%] transition-all duration-300 flex flex-col items-center justify-center gap-[2rem] lg:w-[48.66rem] w-full"
              >
                {/* Icon */}
                <div className="w-[6.875rem] h-[6.875rem] flex items-center justify-center">
                  {social?.icon && (
                    <GroqImage
                      alt={social.label}
                      className="!w-full !h-full !object-contain"
                      src={social?.icon}
                    />
                  )}
                </div>

                {/* Label */}
                {social.label && (
                  <span className="text-[1.875rem] font-newsreader text-[#c4c4c4]  italic uppercase">
                    {social.label}
                  </span>
                )}
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
